import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, authUrl, SEVERITY, eur, pct, CATEGORY_COLORS } from "@/lib/api";
import { locale } from "@/lib/i18n";
import { PageHeader, Stat, SeverityBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n";

const RecCard = ({ r, i }) => {
  const s = SEVERITY[r.severity];
  return (
    <article data-testid={`recommendation-${i}`} className="surface p-4 lg:p-5 fade-up" style={{ animationDelay: `${i * 50}ms`, borderLeft: `3px solid ${s.color}` }}>
      <div className="flex flex-wrap items-center gap-2 mb-2"><SeverityBadge severity={r.severity} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.area}</span></div>
      <h3 className="font-heading font-semibold text-base lg:text-lg">{r.title}</h3>
      <p className="text-sm text-muted-foreground mt-1.5"><span className="text-foreground/70 font-medium">{t("Lettura ·")} </span>{r.reading}</p>
      <p className="text-sm mt-2"><span className="text-primary font-medium">{t("Azione ·")} </span>{r.action}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {r.nodes.map((n) => <Link key={n} to={`/?node=${n}`} className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary">↗ {n}</Link>)}
        {r.files.map((f) => <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-1"><FileText size={10} /> {f}</span>)}
      </div>
    </article>
  );
};

export default function CockpitPage() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/advisor").then((r) => setData(r.data)); api.get("/report/history").then((r) => setReport(r.data)); }, []);
  const sendReport = async () => {
    setSending(true);
    try {
      const r = await api.post("/report/send");
      if (r.data.sent) toast.success(t("Report inviato a {to}", { to: r.data.to })); else toast.error(r.data.error);
      api.get("/report/history").then((x) => setReport(x.data));
    } catch { toast.error(t("Invio non riuscito")); } finally { setSending(false); }
  };
  if (!data) return <div className="p-8 text-muted-foreground">{t("Caricamento cockpit…")}</div>;
  const last = data.last_record;
  const ms = data.menu_summary || {};

  return (
    <div data-testid="cockpit-page">
      <PageHeader eyebrow={t("Direzione F&B · motore decisionale a regole")} title={t("Cockpit direzionale")}
        subtitle={t("Il sistema legge i tuoi consuntivi, il menù e gli eventi pianificati, li confronta con la teoria del corpus e prende posizione.")}>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap justify-end">
            {Object.entries(data.counts).map(([k, v]) => v > 0 && (
              <span key={k} data-testid={`cockpit-count-${k}`} className="text-xs px-3 py-1.5 rounded-full border num" style={{ borderColor: SEVERITY[k].color, color: SEVERITY[k].color }}>{v} {t(SEVERITY[k].label).toLowerCase()}</span>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button asChild size="sm" variant="outline" data-testid="report-pdf-btn"><a href={authUrl("/report/pdf")} target="_blank" rel="noreferrer"><Download size={14} className="mr-1.5" /> {t("Esporta PDF")}</a></Button>
            <Button size="sm" data-testid="report-send-btn" onClick={sendReport} disabled={sending}><Mail size={14} className="mr-1.5" /> {sending ? t("Invio…") : t("Invia report via email")}</Button>
          </div>
          {report && <span data-testid="report-schedule-info" className="text-[11px] text-muted-foreground">{report.owner_email ? t("Invio automatico il 1° del mese a {e}", { e: report.owner_email }) : t("Invio automatico mensile attivo: indica la tua email per riceverlo")}{report.history[0]?.generated_at ? t(" · ultimo report {d}", { d: new Date(report.history[0].generated_at).toLocaleDateString(locale()) }) : ""}</span>}
        </div>
      </PageHeader>

      <div className="px-5 lg:px-8 py-6 space-y-6">
        {last ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat testId="kpi-fc-actual" label={`Food cost ${last.period}`} value={pct(last.actual_pct)} hint={`target ${pct(last.target_pct)}`} accent={SEVERITY[last.status === "ok" ? "ok" : last.status].color} />
            <Stat testId="kpi-fc-budget" label={t("Preventivo")} value={pct(last.budget_pct)} hint={t("scostamento {v} pt", { v: `${last.variance_pts > 0 ? "+" : ""}${last.variance_pts}` })} />
            <Stat testId="kpi-gross-margin" label={t("Margine lordo")} value={eur(last.gross_margin)} hint={pct(last.gross_margin_pct)} />
            <Stat testId="kpi-margin-cover" label={t("Margine / coperto")} value={eur(last.margin_per_cover)} hint={t("ricavo {v}", { v: eur(last.revenue_per_cover) })} />
            <Stat testId="kpi-covers" label={t("Coperti")} value={last.covers.toLocaleString(locale())} hint={t("ricavi {v}", { v: eur(last.revenue) })} />
          </div>
        ) : <div className="surface p-5 text-sm text-muted-foreground">{t("Nessun consuntivo salvato: inserisci il primo periodo nei calcolatori.")}</div>}

        <div className="grid lg:grid-cols-[1fr_300px] gap-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">{t("Letture e raccomandazioni")}</h2><Link to="/team" className="text-xs text-primary flex items-center gap-1">{t("Discussione del team")} <ArrowRight size={12} /></Link></div>
            {data.recommendations.map((r, i) => <RecCard key={i} r={r} i={i} />)}
          </div>
          <aside className="space-y-4">
            <div className="surface p-4">
              <div className="eyebrow mb-3">{t("Matrice menù")}</div>
              {["stella", "cavallo", "enigma", "cane"].map((k) => (
                <div key={k} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 text-sm">
                  <span className="flex items-center gap-2 capitalize"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: CATEGORY_COLORS[k] }} />{t(k === "cavallo" ? "Cavalli di battaglia" : k === "stella" ? "Stelle" : k === "enigma" ? "Enigmi" : "Cani")}</span>
                  <span className="num font-semibold">{ms[k] ?? 0}</span>
                </div>
              ))}
              <Link to="/calcolatori?tab=menu" className="text-xs text-primary mt-3 inline-flex items-center gap-1">{t("Apri menu engineering")} <ArrowRight size={12} /></Link>
            </div>
            <div className="surface p-4">
              <div className="eyebrow mb-3">{t("Eventi pianificati")}</div>
              <div className="num text-3xl font-semibold">{data.events_summary?.total ?? 0}</div>
              <div className="text-xs text-muted-foreground">{t("{n} con alert di logistica o margine", { n: data.events_summary?.con_alert ?? 0 })}</div>
              <Link to="/eventi" className="text-xs text-primary mt-3 inline-flex items-center gap-1">{t("Pianificatore eventi")} <ArrowRight size={12} /></Link>
            </div>
            <div data-testid="cockpit-history-summary" className="surface p-4">
              <div className="eyebrow mb-3">{t("Storico vendite")}</div>
              <div className="num text-3xl font-semibold">{data.history_summary?.periods ?? 0}</div>
              <div className="text-xs text-muted-foreground">{t("periodi di cassa importati")}</div>
              {data.history_summary?.periods >= 2 && (
                <div className="flex gap-3 mt-2 text-xs num">
                  <span data-testid="cockpit-history-up" className="text-emerald-400">↑ {data.history_summary.up} {t("saliti")}</span>
                  <span data-testid="cockpit-history-down" className="text-amber-300">↓ {data.history_summary.down} {t("scesi")}</span>
                </div>
              )}
              <Link to="/calcolatori?tab=menu" className="text-xs text-primary mt-3 inline-flex items-center gap-1">{t("Apri lo storico")} <ArrowRight size={12} /></Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
